import { useMemo } from 'react';
import { addDays, compareAsc, isSameDay } from 'date-fns';
import { getWeekFromDate } from '../model/regular-week/get-week-from-date';
import type {
  RegularWeek,
  RegularWeekDay
} from '../model/regular-week/regular-week';
import { useSettingsSpecialIntervals } from '../model/settings-special-interval/use-settings-special-intervals';
import type { SettingsWeek } from '../model/settings-week/settings-week';
import { useSettingsWeek } from '../model/settings-week/use-settings-week';
import { getSpecialDaysFromDate } from '../model/special-day/get-special-days-from-date';
import type { SpecialDay } from '../model/special-day/special-day';

function getNextIndex(index: number, limit: number) {
  if (index < limit) {
    index += 1;
  }

  return index;
}

export function mergeDays(
  weekDays: RegularWeek = [],
  specialDays: SpecialDay[] = []
) {
  if (weekDays.length === 0) {
    return specialDays;
  }

  if (specialDays.length === 0) {
    return weekDays;
  }

  const result = [];

  let weekDayIndex = 0;
  let specialDayIndex = 0;

  let hasLeftWeekdays = weekDayIndex < weekDays.length;
  let hasLeftSpecialDays = specialDayIndex < specialDays.length;

  while (hasLeftWeekdays || hasLeftSpecialDays) {
    const currWeekday = weekDays[Math.min(weekDayIndex, weekDays.length - 1)];
    const currSpecialDay =
      specialDays[Math.min(specialDayIndex, specialDays.length - 1)];

    const compareAscWeekdayToSpecialDay = compareAsc(
      currWeekday.date,
      currSpecialDay.date
    );

    const weekdayIsLater = compareAscWeekdayToSpecialDay === 1;
    const specialDayIsLater = compareAscWeekdayToSpecialDay === -1;

    const shouldPushSpecialDay =
      !hasLeftWeekdays || (weekdayIsLater && hasLeftSpecialDays);
    const shouldPushWeekDay =
      !hasLeftSpecialDays || (specialDayIsLater && hasLeftWeekdays);

    if (isSameDay(currWeekday.date, currSpecialDay.date)) {
      result.push(currSpecialDay);
      specialDayIndex = getNextIndex(specialDayIndex, specialDays.length);
      weekDayIndex = getNextIndex(weekDayIndex, weekDays.length);
    } else if (shouldPushSpecialDay) {
      result.push(currSpecialDay);
      specialDayIndex = getNextIndex(specialDayIndex, specialDays.length);
    } else if (shouldPushWeekDay) {
      result.push(currWeekday);
      weekDayIndex = getNextIndex(weekDayIndex, weekDays.length);
    }

    hasLeftWeekdays = weekDayIndex < weekDays.length;
    hasLeftSpecialDays = specialDayIndex < specialDays.length;
  }

  return result;
}

function generateWeeksUntilSpecialDays(
  week: SettingsWeek,
  startDate: Date,
  lastSpecialDay: SpecialDay,
  maxWeeks = 12 // Ограничение в ~3 месяца для предотвращения излишней генерации данных
): RegularWeekDay[] {
  let allWeekDays: RegularWeekDay[] = [];
  let currentDate = startDate;

  for (let i = 0; i < maxWeeks; i++) {
    const weekDays = getWeekFromDate(week, currentDate);
    const lastWeekDay = weekDays.at(-1);

    if (!lastWeekDay) {
      break;
    }

    allWeekDays.push(...weekDays);

    const hasCoveredSpecialDays =
      compareAsc(lastWeekDay.date, lastSpecialDay.date) >= 0;

    if (hasCoveredSpecialDays) {
      break;
    }

    currentDate = addDays(lastWeekDay.date, 1);
  }

  return allWeekDays;
}

function getNextWeekAfterLastDay(
  week: SettingsWeek,
  lastDay: RegularWeekDay | SpecialDay
): RegularWeek {
  if (!lastDay) {
    return [];
  }

  return getWeekFromDate(week, addDays(lastDay.date, 1));
}

export function useUpcomingDays(): (RegularWeekDay | SpecialDay)[] {
  const specialIntervals = useSettingsSpecialIntervals();
  const week = useSettingsWeek();

  return useMemo(() => {
    const now = new Date();
    const specialDays = getSpecialDaysFromDate(specialIntervals, now);
    const lastSpecialDay = specialDays.at(-1);

    if (!lastSpecialDay) {
      return getWeekFromDate(week, now);
    }

    const allWeekDays = generateWeeksUntilSpecialDays(
      week,
      now,
      lastSpecialDay
    );

    const days: (RegularWeekDay | SpecialDay)[] = mergeDays(
      allWeekDays,
      specialDays
    );

    const lastDay = days.at(-1);

    if (lastDay) {
      days.push(...getNextWeekAfterLastDay(week, lastDay));
    }

    return days;
  }, [specialIntervals, week]);
}
